import { ApolloQueryResult } from '@apollo/client';
import { SearchActionType } from '@modules/feed/store/searchActionType';
import { useSearchStore } from '@modules/feed/store/searchStore';
import { BaseCondition } from '@modules/feed/types';
import { getSearchWhereClauseFromBaseCondition } from '@modules/feed/utils';
import { Stack } from '@mui/material';
import {
  Aspect,
  GetSearchRecommendationsInput,
  Query,
  Reason,
} from 'generated/graphql';
import { createAnalyticsTracker } from 'v2/analytics/events';
import { AddLabelInterestsBanner } from '../AddLabelInterestsBanner';
import { LabelRecommendations } from './LabelRecommendations';

export const trackSearchV2Events = createAnalyticsTracker('searchV2');

interface BasicSearchRecommendationsProps {
  loading: boolean;
  reasons: Reason[];
  trackedKeywords: Aspect[];
  refetchRecommendations: (
    variables?: Partial<GetSearchRecommendationsInput> | undefined,
  ) => Promise<ApolloQueryResult<Query>>;
}

export function BasicSearchRecommendations({
  loading,
  trackedKeywords,
  reasons,
  refetchRecommendations: refetch,
}: BasicSearchRecommendationsProps) {
  const dispatch = useSearchStore(({ dispatch }) => dispatch);

  const onSelect = (
    baseCondition: BaseCondition | null,
    type: 'trackedKeyword' | 'reason',
  ) => {
    if (!baseCondition) {
      return;
    }
    const whereClause = getSearchWhereClauseFromBaseCondition(baseCondition);
    trackSearchV2Events({
      eventName:
        type === 'trackedKeyword'
          ? 'Recommended Keyword Clicked'
          : 'Recommended Reason Clicked',
    });
    dispatch({
      type: SearchActionType.SET_QUERY,
      payload: {
        whereClause,
      },
    });
  };

  return (
    <Stack width="100%" direction="column" spacing={9}>
      <Stack direction="row" spacing={10} justifyContent="space-between">
        <LabelRecommendations
          title="Recommended Tracked Keywords"
          infoText="Tracked Keywords are how your customers would describe different aspects of your product."
          labels={trackedKeywords}
          onSelect={(baseCondition) =>
            onSelect(baseCondition, 'trackedKeyword')
          }
          isLoading={loading}
          hideTooltip
        />
        <LabelRecommendations
          title="Recommended Feedback Reasons"
          infoText="Reasons are repeatable granular summaries in the voice of the customer. Reasons are why the customer gave the feedback."
          labels={reasons}
          onSelect={(baseCondition) => onSelect(baseCondition, 'reason')}
          isLoading={loading}
        />
      </Stack>
      <AddLabelInterestsBanner
        onUpdate={() => refetch({ shouldRecompute: true })}
      />
    </Stack>
  );
}
